//! Import the necessary dependencies
import { API_URI } from "../../constants/consts"; //* Import API URI from constants
import { api } from "../../services/apis/configs/axiosConfigs"; //* Import API configuration

//* Type for pagination data
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
	Question,
	QuestionDetails,
	CreateQuestionInput,
	UpdateQuestionInput,
	GetQuestionsResponse,
	GetQuestionsDisciplinesResponse,
	QuestionLifecycle,
} from "./types/types";
//? Import the QuestionType type from the form/types/types.tsx file
import { QuestionType } from "../../hooks/questions/types/types";

/**
 * Maps the response data from the API to a Question object.
 *
 * @param data - The raw data from the API response.
 * @returns A Question object with the mapped properties.
 */
export function mapQuestionResponse(data: any): Question {
	const discipline = data.discipline || {
		id: data.discipline_id || "",
		name: data.discipline_name || "-",
	};

	return {
		id: String(data.id ?? ""),
		displayLabel: data.displayLabel || data.display_label || undefined,
		educationLevel: data.educationLevel || data.education_level || "elementary",
		difficultyLevel: data.difficultyLevel || data.difficulty_level || "easy",
		year: data.year ?? null,
		phase: data.phase ?? null,
		discipline,
		createdBy: data.created_by,
		createdByName: data.createdByName ?? data.created_by_name ?? null,
		createdAt: data.created_at ? new Date(data.created_at) : new Date(data.createdAt || Date.now()),
		source: data.source ?? null,
		externalQuestionId: data.externalQuestionId ?? data.external_question_id ?? null,
		tags: Array.isArray(data.tags) ? data.tags : [],
	};
}

/**
 * Maps the response data to a QuestionDetails object.
 *
 * @param data - The response data to map.
 * @returns A QuestionDetails object containing the mapped data.
 */
export function mapQuestionDetailsResponse(data: any): QuestionDetails {
	return {
		id: data.id,
		displayLabel: data.displayLabel || data.display_label || undefined,
		educationLevel: data.educationLevel || data.education_level,
		difficultyLevel: data.difficultyLevel || data.difficulty_level,
		year: data.year ?? null,
		phase: data.phase ?? null,
		statement: data.statement,
		disciplines: data.disciplines || data.discipline,
		subQuestions: data.subQuestions || [],
		tags: data.tags || [],
		createdAt: data.created_at ? new Date(data.created_at) : new Date(data.createdAt || Date.now()),
		updatedAt: data.updated_at ? new Date(data.updated_at) : new Date(data.updatedAt || Date.now()),
		createdBy: data.created_by,
		source: data.source ?? null,
		externalQuestionId: data.externalQuestionId ?? data.external_question_id ?? null,
		examTitle: data.examTitle ?? data.exam_title ?? null,
	};
}

const mapPaginationResponse = (
	pagination: any,
	fallback: { currentPage: number; totalPages: number; itemsPerPage: number; totalItems: number }
): Pagination => {
	if (!pagination) {
		return fallback;
	}

	return {
		currentPage: pagination.currentPage ?? pagination.current_page ?? fallback.currentPage,
		totalPages: pagination.totalPages ?? pagination.total_pages ?? fallback.totalPages,
		itemsPerPage: pagination.itemsPerPage ?? pagination.items_per_page ?? fallback.itemsPerPage,
		totalItems: pagination.totalItems ?? pagination.total_items ?? fallback.totalItems,
	};
};

/**
 * Maps the payload data to the API format.
 *
 * @param data - The data to map.
 * @returns The mapped payload data.
 */
export function mapQuestionPayload(data: any) {
	const payload: any = {
		education_level: data.education_level,
		difficulty: data.difficulty,
		statement: data.statement,
		discipline_id: data.discipline_id,
		source: data.source,
		external_question_id: data.external_question_id,
		exam_year: data.exam_year,
		phase: data.phase,
		'exam_title': data.exam_title,
		sub_questions: data.sub_questions?.map((subQ: any) => {
			// Create a new object to avoid modifying the original data
			const mapped: any = {
				statement: subQ.statement,
				answer_justification: subQ.answer_justification,
				type: subQ.type,
				objectives: subQ.objectives?.map((obj: any) => {
					const objMapped: any = {
						text: obj.text,
						is_correct: obj.is_correct,
						order: obj.order,
					};
					// if the objective has an id, include it
					if (obj.id) {
						objMapped.id = obj.id;
					}
					return objMapped;
				}),
			};

			// If the subquestion has an id, include it
			if (subQ.id) {
				mapped.id = subQ.id;
			}

			return mapped;
		}),
	};

	return payload;
}

/**
 * @Component QuestionAPI
 * @Description Provides CRUD (create, read, update, delete) operations for questions.
 */
export const questionAPI = {
	/**
	 * @function getAll
	 * @description Fetches a paginated list of questions.
	 *
	 * @param {string} [search=""] - Optional search keyword.
	 * @param {number} [page=1] - Page number for pagination.
	 * @param {string} token - Authentication token.
	 * @param {number} [perPage=10] - Items per page.
	 * @param {string} [disciplineId] - Optional discipline ID to filter questions.
	 *
	 * @returns {Promise<GetQuestionsResponse>} - Returns paginated questions data.
	 */
	getAll: async function (
		search: string = "",
		page: number = 1,
		token: string,
		perPage: number = 10,
		disciplineId?: string,
		year?: number,
		phase?: string,
		bancaOnly?: boolean,
		source?: string,
		tag?: string,
		ownOnly?: boolean,
		createdBy?: string
	): Promise<GetQuestionsResponse> {
		const searchQuery = search ? `&search=${encodeURIComponent(search)}` : "";
		const disciplineQuery = disciplineId ? `&discipline_id=${disciplineId}` : "";
		const yearQuery = year ? `&year=${year}` : "";
		const phaseQuery = phase ? `&phase=${encodeURIComponent(phase)}` : "";
		const bancaQuery = bancaOnly ? `&banca_only=1` : "";
		const sourceQuery = source ? `&source=${encodeURIComponent(source)}` : "";
		const tagQuery = tag ? `&tag=${encodeURIComponent(tag)}` : "";
		const ownQuery = ownOnly ? `&own_only=1` : "";
		const createdByQuery = createdBy ? `&created_by=${createdBy}` : "";
		const url = `${API_URI}/admin/private/questions?page=${page}&perPage=${perPage}${searchQuery}${disciplineQuery}${yearQuery}${phaseQuery}${bancaQuery}${sourceQuery}${tagQuery}${ownQuery}${createdByQuery}`;

		return api
			.request({
				url,
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then((response) => {
				const payload = response.data?.data || {};

				const pagination = mapPaginationResponse(payload.pagination, {
					currentPage: 1,
					totalPages: 1,
					itemsPerPage: perPage,
					totalItems: 0,
				});

				const questions: Question[] =
					payload.questions?.map((question: any) =>
						mapQuestionResponse(question)
					) || [];

				return {
					questions,
					pagination,
				};
			})
			.catch((error) => {
				console.error("Error fetching questions:", error);
				throw error;
			});
	},

	/**
	 * @function getById
	 * @description Fetches a question by its ID.
	 *
	 * @param {string} id - The ID of the question to fetch.
	 * @param {string} token - Authentication token.
	 *
	 * @returns {Promise<QuestionDetails>} - Returns the question details.
	 *
	 * @throws {Error} - If the question is not found or the response is invalid.
	 */
	getById: async function (
		id: string,
		token: string
	): Promise<QuestionDetails> {
		const url = `${API_URI}/admin/private/questions/${id}`;

		return api
			.request({
				url,
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then((response) => {
				if (response.data) {
					const data = response.data.data || response.data;
					return mapQuestionDetailsResponse(data);
				}

				throw new Error("Question not found or invalid response.");
			})
			.catch((error) => {
				console.error("Error fetching question:", error);
				throw error;
			});
	},

	/**
	 * @function getLifecycle
	 * @description Fetches the lifecycle analytics of a question (usage across
	 * applications, accuracy/error dispersion, alternative distribution and
	 * daily performance time series). When `applicationId` is provided, the
	 * payload also includes the stats restricted to that single application.
	 *
	 * @param {string} id - The question ID.
	 * @param {string} token - Authentication token.
	 * @param {string} [applicationId] - Optional application filter.
	 *
	 * @returns {Promise<QuestionLifecycle>} - Returns the lifecycle payload.
	 *
	 * @throws {Error} - If the response is invalid.
	 */
	getLifecycle: async function (
		id: string,
		token: string,
		applicationId?: string
	): Promise<QuestionLifecycle> {
		const query = applicationId ? `?application_id=${applicationId}` : "";
		const url = `${API_URI}/admin/private/questions/${id}/lifecycle${query}`;

		return api
			.request({
				url,
				method: "GET",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then((response) => {
				const data = response.data?.data;
				if (!data) {
					throw new Error("Invalid lifecycle response.");
				}
				return data as QuestionLifecycle;
			})
			.catch((error) => {
				console.error("Error fetching question lifecycle:", error);
				throw error;
			});
	},

	/**
	 * @function create
	 * @description Creates a new question.
	 *
	 * @param {CreateQuestionInput} data - The data for the new question.
	 * @param {string} token - Authentication token.
	 *
	 * @returns {Promise<Question>} - Returns the created question.
	 *
	 * @throws {Error} - If there is an error creating the question.
	 */
	create: async function (
		data: CreateQuestionInput,
		token: string
	): Promise<Question> {
		const url = `${API_URI}/admin/private/questions`;

		const payload = mapQuestionPayload(data);

		return api
			.request({
				url,
				method: "POST",
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data: payload,
			})
			.then((response) => mapQuestionResponse(response.data.data))
			.catch((error) => {
				console.error("Error creating question:", error);
				throw error;
			});
	},

	/**
	 * @function update
	 * @description Updates an existing question.
	 *
	 * @param {string} id - The ID of the question to update.
	 * @param {UpdateQuestionInput} data - The updated data for the question.
	 * @param {string} token - Authentication token.
	 *
	 * @returns {Promise<Question>} - Returns the updated question.
	 *
	 * @throws {Error} - If there is an error updating the question.
	 */
	update: async function (
		id: string,
		data: UpdateQuestionInput,
		token: string
	): Promise<Question> {
		const url = `${API_URI}/admin/private/questions/${id}`;
		const payload = mapQuestionPayload(data);

		try {
			const response = await api.request({
				url,
				method: "PUT",
				headers: {
					Authorization: `Bearer ${token}`,
				},
				data: payload,
			});

			return mapQuestionResponse(response.data.data);
		} catch (error: any) {
			console.error("Error updating Question:", error);

			throw new Error("Error updating Question");
		}
	},

	/**
	 * @function delete
	 * @description Deletes a question by its ID.
	 *
	 * @param {string} id - The ID of the question to delete.
	 * @param {string} token - Authentication token.
	 *
	 * @returns {Promise<void>} - Returns nothing.
	 *
	 * @throws {Error} - If there is an error deleting the question.
	 */
	delete: async function (id: string, token: string): Promise<void> {
		const url = `${API_URI}/admin/private/questions/${id}`;

		return api
			.request({
				url,
				method: "DELETE",
				headers: {
					Authorization: `Bearer ${token}`,
				},
			})
			.then(() => { })
			.catch((error) => {
				console.error("Error deleting question:", error);
				throw error;
			});
	},

	/**
	 * @function getAuthors
	 * @description Fetches the distinct authors of the organization's questions,
	 * used to populate the "Criado por" filter.
	 */
	getAuthors: async function (
		token: string
	): Promise<{ id: string; name: string }[]> {
		const url = `${API_URI}/admin/private/questions/authors`;

		return api
			.request({
				url,
				method: "GET",
				headers: { Authorization: `Bearer ${token}` },
			})
			.then((response) => response.data?.data ?? [])
			.catch((error) => {
				console.error("Error fetching question authors:", error);
				throw error;
			});
	},

	/**
	 * @function getByDisciplineId
	 * @description Fetches a paginated list of questions filtered by disciplineId.
	 *
	 * @param {string} disciplineId - Discipline ID to filter questions.
	 * @param {string} token - Authentication token.
	 * @param {number} [page=1] - Page number for pagination (default is 1).
	 * @param {number} [perPage=10] - Items per page (default is 10).
	 * @param {string} questionType - Type of question to filter (e.g., "objective" or "dissertation").
	 * @param {string} [search] - Optional search keyword.
	 * @param {string[]} [tags] - Optional array of tags to filter.
	 *
	 * @returns {Promise<GetQuestionsResponse>} - Returns paginated questions data.
	 *
	 * @throws {Error} - If the question type is invalid or if there is an error fetching the questions.
	 */
	getByDisciplineId: async function (
		disciplineId: string,
		token: string,
		page: number = 1,
		perPage: number = 10,
		questionType: QuestionType,
		search?: string,
		tags?: string[]
	): Promise<GetQuestionsDisciplinesResponse> {


		if (!["objective", "dissertative", "set"].includes(questionType)) {
			throw new Error(
				"Invalid question type. Allowed values are 'objective' and 'dissertative'."
			);
		}

		const params = new URLSearchParams();
		params.append("page", page.toString());
		params.append("perPage", perPage.toString());
		params.append("question_type", questionType.toString());

		if (search) {
			params.append("search", search);
		}

		if (tags && tags.length > 0) {
			params.append("tags", tags.join(","));
		}

		const url = `${API_URI}/admin/private/questions/discipline/${disciplineId}?${params.toString()}`;

		return api
			.request({
				url,
				method: "GET",
				headers: { Authorization: `Bearer ${token}` },
			})
			.then((response) => {
				const payload = response.data?.data || {};

				const pagination = mapPaginationResponse(payload.pagination, {
					currentPage: 1,
					totalPages: 1,
					itemsPerPage: perPage,
					totalItems: 0,
				});

				const questions: QuestionDetails[] =
					payload.questions?.map((question: any) =>
						mapQuestionDetailsResponse(question)
					) || [];

				return {
					questions,
					pagination,
				};
			})
			.catch((error) => {
				console.error("Error fetching questions by discipline:", error);
				throw error;
			});
	},
};
